import { uniq } from "lodash";
import { object, string, array } from "yup";
import {
  numericString,
  stringExists,
  booleanString,
  USPhoneString,
  REQUIRED_FIELD_MESSAGE
} from "./helpers";
import { isFormattedDate, isInAgeRange } from "../";
import { addressFieldsForContact } from "./address";
import {
  isEurope,
  SUPPORTED_EU_COUNTRY_CODES,
  NATIONALITY_SUPPORTED_COUNTRY_CODES
} from "../../countryHelper";

const FISERV_EU_OWNER_PHONE_REGEX = /^\d{5,15}$/;
const FISERV_EU_OWNER_PHONE_MESSAGE =
  "Phone number must be between 5 and 15 digits";
// Fiserv rejects owner phones that are not 10 digits or that start with 1.
const FISERV_AU_OWNER_PHONE_REGEX = /^[02-9]\d{9}$/;
const FISERV_AU_OWNER_PHONE_MESSAGE =
  "Phone number must be 10 digits and cannot start with 1";
const IS_ID_REQUIRED = "$isIDRequired";
const COUNTRY_CODE = "$countryCode";
const FEATURES = "$features";

export const ContactPersonalInformationSchema = object().shape({
  date_of_birth: string().when("$isDateOfBirthRequired", {
    is: true,
    then: stringExists
      .test(
        "format",
        "Please use a YYYY-MM-DD or MM/DD/YYYY format",
        isFormattedDate
      )
      .test("age", "Invalid age", isInAgeRange(18, 100)),
    otherwise: string().notRequired()
  }),
  email: stringExists.email(),
  first_name: stringExists,
  country: string().when(["$authorizedSigner"], (authorizedSigner, schema) => {
    if (authorizedSigner) {
      return schema.ensure().required("Address country is required");
    }

    return schema.notRequired();
  }),
  id_number: string()
    .when(IS_ID_REQUIRED, {
      is: true,
      then: string().ensure(),
      otherwise: string().notRequired()
    })
    .when(
      [IS_ID_REQUIRED, "$initialIdNumbers", "id_type", COUNTRY_CODE],
      (isIDRequired, initialIdNumbers, idType, countryCode, schema) => {
        const idNumbers = initialIdNumbers || [];

        if (!isIDRequired) {
          return schema;
        }

        if (idType === "Passport") {
          return schema.test({
            test: idNumber => !idNumber || idNumber.match(/^[a-zA-Z0-9]+$/),
            message: "Passport number must contain only letters and numbers"
          });
        }

        if (countryCode !== "USA") {
          return schema.test({
            test: idNumber => Boolean(idNumber && idNumber.trim()),
            message: REQUIRED_FIELD_MESSAGE
          });
        }

        return schema.test({
          test: idNumber =>
            !isIDRequired ||
            (idNumbers.length && idNumbers.includes(idNumber)) ||
            idNumber.match(/^\d{3}-?\d{2}-?\d{4}$/),
          message:
            "Please enter a valid United States Social Security Number (SSN)"
        });
      }
    ),
  id_issuer_country: string().when(
    [IS_ID_REQUIRED, "id_type"],
    (isIDRequired, id_type) => {
      if (isIDRequired && id_type === "Passport") {
        return string()
          .ensure()
          .required("Passport issuer country is required")
          .test(
            "not-us-passport",
            "US nationals are required to provide SSN.",
            idIssuerCountry => {
              // Block any US passport - must use SSN instead
              if (idIssuerCountry === "USA") {
                return false;
              }

              return true;
            }
          );
      }
      return string().notRequired();
    }
  ),
  job_title: stringExists,
  last_name: stringExists,
  nationality: string().when(COUNTRY_CODE, {
    is: countryCode => NATIONALITY_SUPPORTED_COUNTRY_CODES.includes(countryCode),
    then: stringExists,
    otherwise: string().notRequired()
  }),
  phone: string().when([COUNTRY_CODE, FEATURES], (countryCode, features) => {
    if (isEurope(countryCode)) {
      return stringExists.matches(FISERV_EU_OWNER_PHONE_REGEX, {
        message: FISERV_EU_OWNER_PHONE_MESSAGE,
        excludeEmptyString: true
      });
    }

    if (countryCode === "USA") {
      return USPhoneString;
    }

    if (countryCode === "AUS" && features?.validateAuPhoneLength) {
      return stringExists.matches(FISERV_AU_OWNER_PHONE_REGEX, {
        message: FISERV_AU_OWNER_PHONE_MESSAGE,
        excludeEmptyString: true
      });
    }

    return stringExists;
  }),
  id_type: string().when([IS_ID_REQUIRED, COUNTRY_CODE], {
    is: (isIDRequired, countryCode) =>
      countryCode === "MYS" ||
      countryCode === "SGP" ||
      countryCode === "HKG" ||
      (isIDRequired && countryCode === "USA"),
    then: string()
      .ensure()
      .matches(
        /(Identity Card|Passport|SOCIAL_SECURITY_NUMBER|ITIN)/,
        "Please select ID Type"
      ),
    otherwise: string().notRequired()
  }),
  address: addressFieldsForContact,
  ever_filed_for_bankruptcy: string().when(COUNTRY_CODE, {
    is: "CAN",
    then: stringExists.oneOf(["0", "1"], "Please select an option")
  })
});

const hasDuplicateIdNumbers = contacts => {
  const idNumbers = contacts
    .filter(contact => contact?.personalInformation?.id_number)
    .map(contact => contact?.personalInformation?.id_number);

  return uniq(idNumbers).length !== idNumbers.length;
};

export const ContactOwnershipSchema = object()
  .shape({
    beneficial_owner: booleanString,
    significant_responsibility: booleanString,
    ownership_percentage: numericString.when("beneficial_owner", {
      is: "1",
      then: numericString
        .min(
          25,
          "Please enter a number between 25 and 100. If this contact owns less that 25% of the business, they do not need to be included on this application."
        )
        .max(100, "Please enter a number between 25 and 100")
        .required("Please fill out this field")
    })
  })
  .when("$authorizedSigner", {
    is: true,
    otherwise: object().test(
      "isBeneficialOwnerOrHasSiginficantResponsibility",
      "Additional business representatives must be a beneficial owner and/or have signficant responsibility",
      ownership =>
        ownership.beneficial_owner === "1" ||
        ownership.significant_responsibility === "1"
    )
  });

export const ContactCollectionSchema = array()
  .min(1, "Please add a business representative")
  .when(COUNTRY_CODE, {
    is: value => ["USA", ...SUPPORTED_EU_COUNTRY_CODES].includes(value),
    then: array().test(
      "hasSignificantResponsibility",
      "Please provide a business representative that holds significant responsibility for managing the business such as CEO, COO, CFO or similar role.",
      contacts =>
        contacts.length === 0 ||
        contacts.some(
          contact => contact.ownership.significant_responsibility === "1"
        )
    )
  })
  .when(COUNTRY_CODE, {
    is: "USA",
    then: array().test(
      "isTotalOwnershipPercentageCorrect",
      "The sum of ownership percentages in benefical owners must be equal or lower to 100.",
      contacts =>
        contacts.reduce(
          (acc, curr) =>
            acc + parseInt(curr.ownership.ownership_percentage || "0", 10),
          0
        ) <= 100
    )
  })
  .when(COUNTRY_CODE, {
    is: "USA",
    then: array().test(
      "isIdNumberRepeated",
      "Tax identifiers must be unique across all contacts.",
      contacts => !hasDuplicateIdNumbers(contacts)
    )
  })
  .when(COUNTRY_CODE, {
    is: "USA",
    then: array().test(
      "hasAtLeastOneUSContact",
      "At least one contact must have a United States address.",
      contacts =>
        contacts.length === 0 ||
        contacts.some(contact => {
          const address = contact?.personalInformation?.address;

          if (!address?.country) {
            return true;
          }

          return (
            address.country === "USA" || address.country === "United States"
          );
        })
    )
  })
  .when(COUNTRY_CODE, {
    is: "NZL",
    then: array().test(
      "hasOwnership",
      "Please provide at least one business representative who owns 25% or more of the business.",
      contacts =>
        contacts.some(contact =>
          Boolean(contact.ownership.ownership_percentage)
        )
    )
  });
